import * as THREE from 'three';
import { GeoBuilder, BOX, CYL, TUBE, CONE, SPH, ICO } from './geom.js';
import { siteMask, TABLEAUX } from './layout.js';
import { surfaceMaterial } from './mat.js';

/**
 * Authored battlefield incidents, baked procedurally into a strict seven-draw
 * world-space kit. The old prop families were individually convincing only at
 * arm's length; at battlefield distance their unrelated instances became
 * beige towers, capsules and confetti. These twelve low tableaux instead put
 * every crate, body, shaft and shield into one readable event direction.
 *
 * Runtime contract:
 *   - two spatial chunks x three shared opaque roles + one scar/contact role
 *   - <= 7 meshes/geometries, <= 4 materials, <= 45k triangles
 *   - no textures, assets, downloads, shadow casters or update hook
 *   - every random decision comes from a private deterministic fork
 */

const TAU = Math.PI * 2;
const SHAFT_R = 0.060; // acceptance: no world-space shaft thinner than 0.055 m
const SCAR_LIFT = 0.022;
const TABLEAU_SEED = 0x7ab1ea;

const ROLE = Object.freeze({ ORGANIC: 0, METAL: 1, CLOTH: 2 });
const ROLE_NAMES = ['organic', 'metal', 'cloth'];
const CHUNK_NAMES = ['west', 'east'];

const C = Object.freeze({
  woodDark: 0x493526,
  wood: 0x5d452d,
  woodPale: 0x806848,
  strawDark: 0x665326,
  straw: 0x927a3d,
  grainDark: 0x80652b,
  grain: 0xb49443,
  flesh: 0x795844,
  leather: 0x4b3425,
  ironDark: 0x343b42,
  iron: 0x68717a,
  ironPale: 0x8a9298,
  bronze: 0x75603a,
  red: 0x76251f,
  redDark: 0x451713,
  blue: 0x294779,
  blueDark: 0x182b4c,
  canvas: 0x6b573a,
  canvasDark: 0x46382a,
  scarEarth: 0x34271f,
  scarBlood: 0x4a1714,
  scarBurn: 0x261f1a,
});

function triCount(geometry) {
  return geometry.index ? geometry.index.count / 3 : geometry.attributes.position.count / 3;
}

/** Tiny triangle-soup compositor for terrain-conforming scars and wheel ruts. */
class ScarBuilder {
  constructor() { this.pos = []; this.col = []; }

  vertex(p, color) {
    const c = new THREE.Color(color);
    this.pos.push(p.x, p.y, p.z);
    this.col.push(c.r, c.g, c.b);
  }

  tri(a, b, c, ca, cb = ca, cc = ca) {
    this.vertex(a, ca); this.vertex(b, cb); this.vertex(c, cc);
  }

  quad(a, b, c, d, ca, cb = ca) {
    this.tri(a, b, c, ca, ca, cb);
    this.tri(a, c, d, ca, cb, cb);
  }

  toGeometry() {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    g.computeBoundingSphere();
    return g;
  }
}

/**
 * @param {object} w game world
 * @param {{camps:Array, battles:Array, fields:Array}} anchors seeded prop sites
 */
export function buildBattlefieldTableaux(w, anchors) {
  const T = w.terrain;
  const placementRng = w.rng.fork(TABLEAU_SEED);
  const group = new THREE.Group();
  group.name = 'battlefield_tableaux';
  group.matrixAutoUpdate = false;
  group.updateMatrix();

  // Six CPU builders become the only opaque render geometries: three material
  // roles for each half of the map. Vertex colour supplies all variation.
  const builders = [
    [new GeoBuilder(), new GeoBuilder(), new GeoBuilder()],
    [new GeoBuilder(), new GeoBuilder(), new GeoBuilder()],
  ];
  const scars = new ScarBuilder();
  const tableaux = [];
  const rolePieces = [0, 0, 0];
  let scarPatches = 0;
  let scarQuads = 0;

  const organicMat = surfaceMaterial({
    wrap: 0.36, hemi: 1.26, spec: [12, 0.018], floor: 0x2b3543,
  });
  organicMat.name = 'tableaux_organic';
  const metalMat = surfaceMaterial({
    wrap: 0.24, hemi: 1.10, spec: [30, 0.095], floor: 0x27313d,
  });
  metalMat.name = 'tableaux_metal';
  const clothMat = surfaceMaterial({
    wrap: 0.42, hemi: 1.32, spec: [10, 0.012], floor: 0x303949,
    side: THREE.DoubleSide,
  });
  clothMat.name = 'tableaux_cloth';
  const scarMat = new THREE.MeshBasicMaterial({
    name: 'tableaux_scar_contact', color: 0xffffff, vertexColors: true,
    transparent: true, opacity: 0.48, depthWrite: false, depthTest: true,
    side: THREE.DoubleSide, fog: true,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
  });
  const materials = [organicMat, metalMat, clothMat, scarMat];

  // One temporary procedural primitive. It is copied into the six final
  // builders, then disposed before the first frame; it never reaches renderer
  // memory and does not add a draw or material.
  const wheelRim = new THREE.TorusGeometry(1, 0.13, 4, 10);
  const qHeading = new THREE.Quaternion();
  const qLocal = new THREE.Quaternion();
  const qWorld = new THREE.Quaternion();
  const eLocal = new THREE.Euler();
  const mtx = new THREE.Matrix4();
  const pos = new THREE.Vector3();
  const scale = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);
  const lineA = new THREE.Vector3();
  const lineB = new THREE.Vector3();
  const lineMid = new THREE.Vector3();
  const lineDir = new THREE.Vector3();

  function rotateXZ(cx, cz, heading, lx, lz, out) {
    const ca = Math.cos(heading), sa = Math.sin(heading);
    out.x = cx + lx * ca + lz * sa;
    out.z = cz - lx * sa + lz * ca;
    return out;
  }

  function groundPoint(ctx, lx, ly, lz, out = new THREE.Vector3()) {
    rotateXZ(ctx.x, ctx.z, ctx.heading, lx, lz, out);
    out.y = T.heightAt(out.x, out.z) + ly;
    return out;
  }

  function put(ctx, role, geo, color, lx, ly, lz,
    rx = 0, ry = 0, rz = 0, sx = 1, sy = sx, sz = sx) {
    groundPoint(ctx, lx, ly, lz, pos);
    qHeading.setFromAxisAngle(up, ctx.heading);
    qLocal.setFromEuler(eLocal.set(rx, ry, rz));
    qWorld.multiplyQuaternions(qHeading, qLocal);
    mtx.compose(pos, qWorld, scale.set(sx, sy, sz));
    builders[ctx.chunk][role].add(geo, mtx, color);
    rolePieces[role]++;
  }

  function beam(ctx, role, color, ax, ay, az, bx, by, bz, radius = SHAFT_R, seg = 6) {
    groundPoint(ctx, ax, ay, az, lineA);
    groundPoint(ctx, bx, by, bz, lineB);
    lineDir.subVectors(lineB, lineA);
    const len = lineDir.length();
    if (len < 0.001) return;
    lineDir.multiplyScalar(1 / len);
    lineMid.addVectors(lineA, lineB).multiplyScalar(0.5);
    qWorld.setFromUnitVectors(up, lineDir);
    mtx.compose(lineMid, qWorld, scale.set(radius, len, radius));
    builders[ctx.chunk][role].add(CYL(seg, 1), mtx, color);
    rolePieces[role]++;
  }

  function spear(ctx, lx, lz, length, yaw, clothColor = 0, broken = false) {
    const dx = Math.cos(yaw), dz = Math.sin(yaw);
    const half = length * 0.5;
    const bx = lx - dx * half, bz = lz - dz * half;
    const ex = lx + dx * half, ez = lz + dz * half;
    const tipBaseX = ex - dx * 0.08, tipBaseZ = ez - dz * 0.08;
    beam(ctx, ROLE.ORGANIC, broken ? C.woodDark : C.wood, bx, SHAFT_R + 0.018, bz,
      tipBaseX, SHAFT_R + 0.018, tipBaseZ, SHAFT_R, 6);
    if (!broken) {
      groundPoint(ctx, tipBaseX, 0.10, tipBaseZ, lineA);
      groundPoint(ctx, ex + dx * 0.30, 0.10, ez + dz * 0.30, lineB);
      lineDir.subVectors(lineB, lineA);
      const len = lineDir.length();
      lineDir.multiplyScalar(1 / len);
      lineMid.addVectors(lineA, lineB).multiplyScalar(0.5);
      qWorld.setFromUnitVectors(up, lineDir);
      mtx.compose(lineMid, qWorld, scale.set(0.115, len, 0.115));
      builders[ctx.chunk][ROLE.METAL].add(CONE(5), mtx, C.ironPale);
      rolePieces[ROLE.METAL]++;
    }
    if (clothColor) {
      const side = 0.24;
      const px = ex - dx * 0.42, pz = ez - dz * 0.42;
      const nx = -dz, nz = dx;
      const a = groundPoint(ctx, px, 0.115, pz);
      const b = groundPoint(ctx, px - dx * 0.58 + nx * side, 0.10, pz - dz * 0.58 + nz * side);
      const c = groundPoint(ctx, px - dx * 0.58 - nx * side, 0.10, pz - dz * 0.58 - nz * side);
      const d = groundPoint(ctx, px - dx * 0.08, 0.115, pz - dz * 0.08);
      builders[ctx.chunk][ROLE.CLOTH].quad(a, b, c, d, clothColor);
      rolePieces[ROLE.CLOTH]++;
    }
  }

  function crate(ctx, lx, lz, yaw, size = 1, level = 0) {
    const h = 0.66 * size;
    const y = 0.035 + h * 0.5 + level * h * 0.94;
    put(ctx, ROLE.ORGANIC, BOX(), C.woodPale, lx, y, lz, 0, yaw, 0,
      0.96 * size, h, 0.78 * size);
    put(ctx, ROLE.ORGANIC, BOX(), C.woodDark, lx, y + h * 0.12, lz, 0, yaw, 0,
      1.00 * size, 0.095, 0.82 * size);
    for (const side of [-1, 1]) {
      const ca = Math.cos(yaw), sa = Math.sin(yaw);
      const ox = -sa * side * 0.29 * size, oz = ca * side * 0.29 * size;
      put(ctx, ROLE.ORGANIC, BOX(), C.wood, lx + ox, y, lz + oz, 0, yaw, 0,
        0.10, h * 1.03, 0.84 * size);
    }
  }

  function barrel(ctx, lx, lz, yaw, lying = false) {
    const rx = lying ? Math.PI * 0.5 : 0;
    const y = lying ? 0.42 : 0.51;
    put(ctx, ROLE.ORGANIC, CYL(9, 1.12), C.wood, lx, y, lz, rx, yaw, 0,
      0.37, 0.92, 0.37);
    for (const o of [-0.34, 0, 0.34]) {
      const ox = lying ? 0 : 0;
      const oy = lying ? 0 : o;
      const oz = lying ? o : 0;
      put(ctx, ROLE.METAL, TUBE(9, 1), C.ironDark, lx + ox, y + oy, lz + oz,
        rx, yaw, 0, 0.405, 0.085, 0.405);
    }
  }

  function sack(ctx, lx, lz, yaw, color = C.canvas) {
    put(ctx, ROLE.CLOTH, ICO(1), color, lx, 0.38, lz, 0.10, yaw, -0.08,
      0.70, 0.76, 0.54);
    put(ctx, ROLE.ORGANIC, TUBE(6, 1), C.leather, lx, 0.72, lz, 0, yaw, 0,
      0.16, 0.07, 0.16);
  }

  /** A tied grain sheaf made from visibly separate angular stalks, never a bale/capsule. */
  function sheaf(ctx, lx, lz, yaw, size = 1) {
    const dx = Math.cos(yaw), dz = Math.sin(yaw);
    const nx = -dz, nz = dx;
    for (let i = 0; i < 5; i++) {
      const side = (i - 2) * 0.075 * size;
      const lift = 0.43 + Math.abs(i - 2) * 0.018;
      const fan = (i - 2) * 0.035;
      put(ctx, ROLE.ORGANIC, BOX(), i & 1 ? C.straw : C.strawDark,
        lx + nx * side, lift, lz + nz * side,
        0, yaw + fan, (i - 2) * 0.022, 0.86 * size, 0.065, 0.065);
    }
    put(ctx, ROLE.ORGANIC, ICO(0), C.straw, lx + dx * 0.46 * size, 0.45,
      lz + dz * 0.46 * size, 0.1, yaw, 0.18, 0.28 * size, 0.18, 0.22 * size);
    put(ctx, ROLE.CLOTH, BOX(), C.canvasDark, lx, 0.45, lz,
      0, yaw, 0, 0.13, 0.17, 0.37 * size);
  }

  function wheel(ctx, lx, lz, y, yaw = 0, lean = 0, broken = false, size = 1) {
    // Rim plane is local XY, its axle points along local Z.
    put(ctx, ROLE.ORGANIC, wheelRim, C.woodDark, lx, y, lz,
      lean, yaw, 0, 0.60 * size, 0.60 * size, 0.60 * size);
    const spokes = broken ? 4 : 6;
    for (let i = 0; i < spokes; i++) {
      const a = (i / spokes) * TAU + (broken ? 0.18 : 0);
      put(ctx, ROLE.ORGANIC, BOX(), i & 1 ? C.wood : C.woodPale, lx, y, lz,
        lean, yaw, a, 0.075 * size, 1.05 * size, 0.065 * size);
    }
    put(ctx, ROLE.METAL, CYL(7, 1), C.ironDark, lx, y, lz,
      Math.PI * 0.5 + lean, yaw, 0, 0.15 * size, 0.22 * size, 0.15 * size);
  }

  function shield(ctx, lx, lz, yaw, color, lean = 0.18, size = 1) {
    const y = 0.13 * size + Math.sin(Math.min(1.2, Math.abs(lean))) * 0.26 * size;
    put(ctx, ROLE.CLOTH, CYL(10, 1), color, lx, y, lz,
      lean, yaw, 0, 0.54 * size, 0.075 * size, 0.54 * size);
    put(ctx, ROLE.METAL, CYL(7, 1), C.iron, lx, y + 0.045 * size, lz,
      lean, yaw, 0, 0.16 * size, 0.105 * size, 0.16 * size);
  }

  /** Route-scale shield planted upright so its silhouette survives a crowd. */
  function standingShield(ctx, lx, lz, yaw, color, size = 1, fall = 0) {
    const y = 0.78 * size;
    put(ctx, ROLE.CLOTH, CYL(10, 1), color, lx, y, lz,
      Math.PI * 0.5 + fall, yaw, 0, 0.62 * size, 0.075 * size, 0.78 * size);
    // A dark raised rim turns the low-poly face into a layered field shield.
    // The torus is already shared by the wagon wheels and adds no geometry or
    // material owner at runtime.
    put(ctx, ROLE.METAL, wheelRim, C.ironDark, lx, y, lz,
      fall, yaw, 0, 0.58 * size, 0.72 * size, 0.09 * size);
    put(ctx, ROLE.METAL, CYL(8, 1), C.iron, lx, y + 0.015 * size, lz,
      Math.PI * 0.5 + fall, yaw, 0, 0.17 * size, 0.11 * size, 0.17 * size);
    // A dark vertical grip/rib keeps the disc from reading as a flat coin.
    put(ctx, ROLE.ORGANIC, BOX(), C.woodDark, lx, y, lz - 0.045,
      fall, yaw, 0, 0.085 * size, 0.92 * size, 0.075 * size);
  }

  /** Tall plank pavise used to break repeated round-shield silhouettes. */
  function standingPavise(ctx, lx, lz, yaw, color, size = 1, fall = 0) {
    const y = 0.82 * size;
    put(ctx, ROLE.CLOTH, BOX(), color, lx, y, lz,
      fall, yaw, 0, 1.04 * size, 1.56 * size, 0.13 * size);
    put(ctx, ROLE.ORGANIC, BOX(), C.woodDark, lx, y, lz - 0.055,
      fall, yaw, 0, 0.11 * size, 1.42 * size, 0.18 * size);
    put(ctx, ROLE.ORGANIC, BOX(), C.wood, lx, y + 0.34 * size, lz - 0.06,
      fall, yaw, 0, 0.90 * size, 0.10 * size, 0.18 * size);
    put(ctx, ROLE.METAL, CYL(8, 1), C.iron, lx, y + 0.02 * size, lz,
      Math.PI * 0.5 + fall, yaw, 0, 0.16 * size, 0.14 * size, 0.16 * size);
  }

  /** A low field litter with a visible cloth sling and paired carrying rails. */
  function litter(ctx, lx, lz, yaw, color, size = 1) {
    const dx = Math.cos(yaw), dz = Math.sin(yaw);
    const nx = -dz, nz = dx;
    const half = 1.34 * size, side = 0.38 * size;
    for (const s of [-1, 1]) {
      beam(ctx, ROLE.ORGANIC, C.wood,
        lx - dx * half + nx * side * s, 0.22, lz - dz * half + nz * side * s,
        lx + dx * half + nx * side * s, 0.22, lz + dz * half + nz * side * s,
        0.075, 6);
    }
    const a = groundPoint(ctx, lx - dx * 1.05 * size + nx * side, 0.25,
      lz - dz * 1.05 * size + nz * side);
    const b = groundPoint(ctx, lx + dx * 1.05 * size + nx * side, 0.25,
      lz + dz * 1.05 * size + nz * side);
    const c = groundPoint(ctx, lx + dx * 1.05 * size - nx * side, 0.25,
      lz + dz * 1.05 * size - nz * side);
    const d = groundPoint(ctx, lx - dx * 1.05 * size - nx * side, 0.25,
      lz - dz * 1.05 * size - nz * side);
    builders[ctx.chunk][ROLE.CLOTH].quad(a, b, c, d, color);
    rolePieces[ROLE.CLOTH]++;
  }

  function corpse(ctx, lx, lz, yaw, color, pose = 0) {
    const dx = Math.cos(yaw), dz = Math.sin(yaw);
    const nx = -dz, nz = dx;
    put(ctx, ROLE.CLOTH, ICO(0), color, lx, 0.24, lz, 0.08, yaw, pose * 0.05,
      1.12, 0.58, 0.78);
    put(ctx, ROLE.METAL, BOX(), C.ironDark, lx + dx * 0.02, 0.42, lz + dz * 0.02,
      0.08, yaw, pose * 0.05, 0.62, 0.075, 0.54);
    put(ctx, ROLE.ORGANIC, SPH(6, 4), C.flesh, lx - dx * 0.72, 0.21, lz - dz * 0.72,
      0, yaw, 0, 0.50, 0.42, 0.46);

    const knee = pose & 1 ? 0.42 : -0.34;
    beam(ctx, ROLE.CLOTH, color, lx + dx * 0.38 + nx * 0.23, 0.17, lz + dz * 0.38 + nz * 0.23,
      lx + dx * 0.92 + nx * knee, 0.14, lz + dz * 0.92 + nz * knee, 0.13, 6);
    beam(ctx, ROLE.CLOTH, color, lx + dx * 0.38 - nx * 0.23, 0.16, lz + dz * 0.38 - nz * 0.23,
      lx + dx * 1.00 - nx * knee * 0.55, 0.13, lz + dz * 1.00 - nz * knee * 0.55, 0.13, 6);
    beam(ctx, ROLE.ORGANIC, C.flesh, lx - dx * 0.12 + nx * 0.40, 0.15, lz - dz * 0.12 + nz * 0.40,
      lx - dx * 0.48 + nx * 0.87, 0.11, lz - dz * 0.48 + nz * 0.87, 0.10, 5);
  }

  function tarp(ctx, color, x0, z0, x1, z1, ridgeY, eaveY) {
    const midX = (x0 + x1) * 0.5;
    const dark = new THREE.Color(color).multiplyScalar(0.70).getHex();
    const bays = 3;
    for (let i = 0; i < bays; i++) {
      const t0 = i / bays, t1 = (i + 1) / bays;
      const za = z0 + (z1 - z0) * t0, zb = z0 + (z1 - z0) * t1;
      const sagA = Math.sin(t0 * Math.PI) * 0.10;
      const sagB = Math.sin(t1 * Math.PI) * 0.10;
      const ridgeA = groundPoint(ctx, midX + (i & 1 ? 0.035 : -0.025), ridgeY - sagA, za);
      const ridgeB = groundPoint(ctx, midX + (i & 1 ? -0.025 : 0.035), ridgeY - sagB, zb);
      const leftA = groundPoint(ctx, x0 + sagA * 0.18, eaveY - sagA * 0.35, za);
      const leftB = groundPoint(ctx, x0 + sagB * 0.18, eaveY - sagB * 0.35, zb);
      const rightA = groundPoint(ctx, x1 - sagA * 0.18, eaveY - sagA * 0.35, za);
      const rightB = groundPoint(ctx, x1 - sagB * 0.18, eaveY - sagB * 0.35, zb);
      builders[ctx.chunk][ROLE.CLOTH].quad(leftA, ridgeA, ridgeB, leftB, i & 1 ? dark : color);
      builders[ctx.chunk][ROLE.CLOTH].quad(ridgeA, rightA, rightB, ridgeB, i & 1 ? color : dark);
      rolePieces[ROLE.CLOTH] += 2;
    }
    // Raised seams catch a thin highlight and break the remaining broad plane.
    put(ctx, ROLE.CLOTH, BOX(), dark, midX, ridgeY - 0.055, (z0 + z1) * 0.5,
      0, 0, 0, 0.075, 0.045, Math.abs(z1 - z0));
    put(ctx, ROLE.CLOTH, BOX(), dark, x0, eaveY, (z0 + z1) * 0.5,
      0, 0, 0, 0.065, 0.045, Math.abs(z1 - z0));
    put(ctx, ROLE.CLOTH, BOX(), dark, x1, eaveY, (z0 + z1) * 0.5,
      0, 0, 0, 0.065, 0.045, Math.abs(z1 - z0));
  }

  function localScarPoint(ctx, lx, lz) {
    const p = groundPoint(ctx, lx, SCAR_LIFT, lz);
    return p;
  }

  function scarPatch(ctx, rx, rz, innerColor, outerColor) {
    // Battlefield damage is a set of interrupted gouges, not a flat oval decal.
    // The gaps keep the terrain visible through the incident and the asymmetric
    // directions still ground the large silhouette from high and live cameras.
    const arms = 7;
    for (let i = 0; i < arms; i++) {
      const a = (i / arms) * TAU + ctx.rng.range(-0.17, 0.17);
      const ca = Math.cos(a), sa = Math.sin(a);
      const r0 = 0.10 + ctx.rng.next() * 0.16;
      const r1 = 0.50 + ctx.rng.next() * 0.36;
      const bend = ctx.rng.range(-0.16, 0.16);
      const mx = ca * rx * (r0 + (r1 - r0) * 0.46) - sa * bend * rx;
      const mz = sa * rz * (r0 + (r1 - r0) * 0.46) + ca * bend * rz;
      scarStrip(ctx, ca * rx * r0, sa * rz * r0,
        mx, mz, 0.08 + ctx.rng.next() * 0.08, innerColor);
      // A small deliberate break prevents the two halves becoming a radial pie.
      scarStrip(ctx, mx + ca * 0.18, mz + sa * 0.18,
        ca * rx * r1, sa * rz * r1,
        0.06 + ctx.rng.next() * 0.07, i & 1 ? outerColor : innerColor);
    }
    // Three low, skewed contact pads sit under the event's heaviest structures.
    for (let i = 0; i < 3; i++) {
      const x = ctx.rng.range(-rx * 0.42, rx * 0.42);
      const z = ctx.rng.range(-rz * 0.42, rz * 0.42);
      const sx = 0.34 + ctx.rng.next() * 0.42;
      const sz = 0.20 + ctx.rng.next() * 0.30;
      const a = localScarPoint(ctx, x - sx, z - sz * 0.55);
      const b = localScarPoint(ctx, x + sx * 0.72, z - sz);
      const c = localScarPoint(ctx, x + sx, z + sz * 0.52);
      const d = localScarPoint(ctx, x - sx * 0.82, z + sz);
      scars.quad(a, b, c, d, innerColor, outerColor);
      scarQuads++;
    }
    scarPatches++;
  }

  function scarStrip(ctx, lx0, lz0, lx1, lz1, width, color) {
    const dx = lx1 - lx0, dz = lz1 - lz0;
    const length = Math.max(0.001, Math.hypot(dx, dz));
    const inv = 1 / length;
    const nx = -dz * inv * width, nz = dx * inv * width;
    const segments = Math.max(1, Math.ceil(length / 1.20));
    for (let i = 0; i < segments; i++) {
      const t0 = i / segments, t1 = (i + 1) / segments;
      const ax = lx0 + dx * t0, az = lz0 + dz * t0;
      const bx = lx0 + dx * t1, bz = lz0 + dz * t1;
      const a = localScarPoint(ctx, ax + nx, az + nz);
      const b = localScarPoint(ctx, bx + nx, bz + nz);
      const c = localScarPoint(ctx, bx - nx, bz - nz);
      const d = localScarPoint(ctx, ax - nx, az - nz);
      scars.quad(a, b, c, d, color);
      scarQuads++;
    }
  }

  function faction(i) { return i & 1 ? C.red : C.blue; }
  function factionDark(i) { return i & 1 ? C.redDark : C.blueDark; }

  function brokenWagonSupply(ctx) {
    scarPatch(ctx, 3.6, 2.5, C.scarEarth, C.scarBurn);
    scarStrip(ctx, -3.8, -0.82, 3.2, -0.82, 0.12, C.scarEarth);
    scarStrip(ctx, -3.8, 0.82, 3.2, 0.82, 0.12, C.scarEarth);
    put(ctx, ROLE.ORGANIC, BOX(), C.wood, -0.25, 0.62, 0, 0.10, -0.08, -0.08, 2.8, 0.28, 1.38);
    put(ctx, ROLE.ORGANIC, BOX(), C.woodDark, -0.25, 0.86, -0.63, 0.10, -0.08, -0.08, 2.9, 0.18, 0.13);
    put(ctx, ROLE.ORGANIC, BOX(), C.woodDark, -0.25, 0.86, 0.63, 0.10, -0.08, -0.08, 2.9, 0.18, 0.13);
    wheel(ctx, -0.75, -0.82, 0.64, 0, 0.08, false);
    wheel(ctx, 0.96, 1.55, 0.63, 0.45, 0.95, true);
    beam(ctx, ROLE.ORGANIC, C.wood, 1.0, 0.32, -0.42, 4.3, 0.12, -0.62, 0.085, 6);
    beam(ctx, ROLE.ORGANIC, C.wood, 1.0, 0.32, 0.42, 4.2, 0.12, 0.64, 0.085, 6);
    crate(ctx, -1.15, 1.02, 0.25, 0.92, 0);
    crate(ctx, -2.02, 1.48, -0.18, 0.78, 0);
    crate(ctx, -1.10, -1.42, 0.62, 0.72, 0);
    barrel(ctx, 1.25, -1.34, 0.22, true);
    tarp(ctx, C.redDark, -1.25, -0.42, 0.58, 0.35, 0.78, 0.50);
  }

  function canvasFieldCache(ctx) {
    scarPatch(ctx, 4.70, 3.45, C.scarEarth, C.scarBurn);

    // An open-front indigo command pavilion replaces the former roof-on-sticks
    // cache. The 7.8 m span and 4.7 m ridge stay identifiable behind bodies.
    tarp(ctx, C.blueDark, -3.90, -2.70, 3.90, 2.70, 4.70, 2.28);
    for (const z of [-2.70, 2.70]) {
      beam(ctx, ROLE.ORGANIC, C.woodDark, 0, 0.04, z, 0, 4.74, z, 0.13, 8);
      for (const x of [-3.90, 3.90]) {
        beam(ctx, ROLE.ORGANIC, C.wood, x, 0.04, z, x, 2.35, z, 0.105, 7);
      }
    }
    beam(ctx, ROLE.ORGANIC, C.woodPale, 0, 4.72, -2.78,
      0, 4.72, 2.78, 0.13, 8);

    // A back wall and short returns make the open mouth legible in silhouette.
    const wa = groundPoint(ctx, -3.72, 0.12, -2.66);
    const wb = groundPoint(ctx, 3.72, 0.12, -2.66);
    const wc = groundPoint(ctx, 3.72, 2.22, -2.66);
    const wd = groundPoint(ctx, -3.72, 2.22, -2.66);
    builders[ctx.chunk][ROLE.CLOTH].quad(wa, wb, wc, wd, C.blueDark, C.blue);
    rolePieces[ROLE.CLOTH]++;
    for (const side of [-1, 1]) {
      const x = side * 3.78;
      const a = groundPoint(ctx, x, 0.12, -2.58);
      const b = groundPoint(ctx, x, 0.12, 0.20);
      const c = groundPoint(ctx, x, 2.18, 0.20);
      const d = groundPoint(ctx, x, 2.20, -2.58);
      builders[ctx.chunk][ROLE.CLOTH].quad(a, b, c, d,
        side < 0 ? C.blueDark : C.blue, C.blueDark);
      rolePieces[ROLE.CLOTH]++;
    }

    // Offset signal standard gives a five-metre vertical read without closing
    // the pavilion's hero-facing entrance.
    beam(ctx, ROLE.ORGANIC, C.woodPale, -3.26, 0.06, -2.32,
      -3.26, 5.02, -2.32, 0.09, 7);
    const fa = groundPoint(ctx, -3.20, 4.88, -2.30);
    const fb = groundPoint(ctx, -1.56, 4.42, -2.30);
    const fc = groundPoint(ctx, -1.88, 3.38, -2.30);
    const fd = groundPoint(ctx, -3.20, 3.72, -2.30);
    builders[ctx.chunk][ROLE.CLOTH].quad(fa, fb, fc, fd, C.blue, C.blueDark);
    rolePieces[ROLE.CLOTH]++;

    crate(ctx, -1.62, -1.22, 0.10, 1.18, 0);
    crate(ctx, 0.02, -1.18, -0.12, 1.02, 0);
    crate(ctx, 1.48, -0.88, 0.18, 0.92, 0);
    sack(ctx, -1.28, 0.52, -0.20, C.canvasDark);
    sack(ctx, -0.28, 0.72, 0.16, C.canvas);
    barrel(ctx, 2.34, 0.02, 0.10, false);
  }

  function harvestSledge(ctx) {
    scarPatch(ctx, 3.8, 2.0, C.scarEarth, C.scarBurn);
    scarStrip(ctx, -3.6, -0.72, 3.6, -0.72, 0.10, C.scarEarth);
    scarStrip(ctx, -3.6, 0.72, 3.6, 0.72, 0.10, C.scarEarth);
    for (const z of [-0.72, 0.72]) beam(ctx, ROLE.ORGANIC, C.woodDark,
      -2.6, 0.13, z, 2.8, 0.13, z, 0.11, 7);
    for (let x = -1.9; x <= 1.9; x += 0.76) beam(ctx, ROLE.ORGANIC, C.wood,
      x, 0.28, -0.88, x, 0.28, 0.88, 0.075, 5);
    for (let i = 0; i < 7; i++) {
      const x = -1.55 + (i % 4) * 0.92;
      const z = -0.38 + ((i / 4) | 0) * 0.82;
      sheaf(ctx, x, z, (i - 3) * 0.025, 1);
    }
    sack(ctx, 2.55, -1.22, 0.4, C.canvas);
    put(ctx, ROLE.METAL, BOX(), C.ironPale, 2.3, 0.13, 1.02,
      0.05, 0.22, 1.20, 0.10, 1.05, 0.035);
    put(ctx, ROLE.METAL, CONE(5), C.iron, 2.74, 0.17, 1.24,
      Math.PI * 0.5, 0.22, 0, 0.16, 0.32, 0.16);
  }

  function fieldRepairYard(ctx) {
    scarPatch(ctx, 5.15, 3.65, C.scarEarth, C.scarBurn);
    scarStrip(ctx, -3.90, -1.78, 3.95, -1.78, 0.15, C.scarBurn);
    scarStrip(ctx, -3.90, 1.78, 3.95, 1.78, 0.15, C.scarBurn);

    // Paired 3.8 m A-frames carry a seven-metre ridge and a hero-facing full
    // wagon wheel. At live distance this reads as a hoist, not a timber rack.
    for (const z of [-1.78, 1.78]) {
      beam(ctx, ROLE.ORGANIC, C.woodDark, -2.08, 0.08, z,
        0, 3.76, z, 0.15, 8);
      beam(ctx, ROLE.ORGANIC, C.woodDark, 2.08, 0.08, z,
        0, 3.76, z, 0.15, 8);
      beam(ctx, ROLE.ORGANIC, C.wood, -1.72, 0.82, z,
        1.72, 0.82, z, 0.12, 7);
    }
    beam(ctx, ROLE.ORGANIC, C.woodPale, 0, 3.76, -2.02,
      0, 3.76, 2.02, 0.17, 9);
    beam(ctx, ROLE.METAL, C.iron, 0, 2.08, -1.92,
      0, 2.08, 1.92, 0.17, 8);
    beam(ctx, ROLE.METAL, C.ironDark, 0, 3.72, 1.78,
      0, 2.72, 1.78, 0.065, 6);
    wheel(ctx, 0, 1.82, 1.82, 0, 0, false, 1.55);
    wheel(ctx, 2.98, -0.92, 0.74, -0.20, 0.94, true, 1.15);

    // A collapsed cart chassis makes the repair context explicit.
    put(ctx, ROLE.ORGANIC, BOX(), C.wood, -2.72, 0.44, -0.32,
      0.18, -0.24, -0.18, 2.30, 0.24, 1.12);
    beam(ctx, ROLE.ORGANIC, C.woodDark, -4.12, 0.16, -0.92,
      -1.28, 0.18, -0.64, 0.11, 7);
    beam(ctx, ROLE.ORGANIC, C.woodDark, -4.10, 0.16, 0.62,
      -1.22, 0.18, 0.54, 0.11, 7);
    tarp(ctx, C.blueDark, -3.92, -2.92, -1.28, -1.12, 0.30, 0.085);
    crate(ctx, 2.92, -1.08, -0.22, 0.96, 0);
    put(ctx, ROLE.METAL, BOX(), C.ironPale, 2.62, 0.11, 0.12,
      0.04, -0.35, 1.12, 0.15, 1.34, 0.060);
  }

  function abandonedCheckpoint(ctx) {
    scarPatch(ctx, 3.5, 2.45, C.scarEarth, C.scarBurn);
    // A proper gate silhouette replaces the former waist-high stick bundle.
    for (const x of [-2.62, 2.62]) {
      beam(ctx, ROLE.ORGANIC, C.woodDark, x, 0.06, 0, x, 2.92, 0, 0.14, 7);
      put(ctx, ROLE.ORGANIC, CONE(5), C.scarBurn, x, 3.10, 0,
        0, 0, 0, 0.19, 0.38, 0.19);
    }
    beam(ctx, ROLE.ORGANIC, C.woodPale, -2.68, 2.70, 0,
      2.68, 2.70, 0, 0.13, 8);
    const ga = groundPoint(ctx, -1.74, 2.52, -0.03);
    const gb = groundPoint(ctx, 1.74, 2.52, -0.03);
    const gc = groundPoint(ctx, 1.36, 1.42, -0.03);
    const gd = groundPoint(ctx, -1.58, 1.56, -0.03);
    builders[ctx.chunk][ROLE.CLOTH].quad(ga, gb, gc, gd, C.redDark);
    rolePieces[ROLE.CLOTH]++;

    // One broken barrier hangs diagonally from the gate while its mate lies
    // clear of the opening: readable abandonment, not a repeated X fence.
    beam(ctx, ROLE.ORGANIC, C.woodDark, -2.48, 1.34, -0.18,
      1.82, 0.18, 0.32, 0.12, 7);
    beam(ctx, ROLE.ORGANIC, C.wood, -2.18, 0.12, 1.18,
      2.34, 0.12, 1.48, 0.09, 6);
    beam(ctx, ROLE.ORGANIC, C.woodDark, -0.94, 0.10, 1.05,
      -0.20, 1.02, 1.42, 0.085, 6);
    shield(ctx, -1.56, -0.78, -0.10, C.redDark, 0.70);
    crate(ctx, 2.08, 1.12, 0.38, 0.88, 0);
    spear(ctx, 0.76, 1.18, 3.35, 0.06, C.redDark, false);
  }

  function grainSpill(ctx) {
    // Compose the visible cart/spill inboard in world X and slightly deeper.
    // This keeps the joined wheel/cart/gold fan inside the two packed
    // cam_crowd framings instead of clipping it at the lens or hiding it under
    // the nearest foreground rank.
    // The authored anchor continues to own deterministic placement telemetry.
    const originX = ctx.x, originZ = ctx.z;
    ctx.x = originX - 5.50;
    ctx.z = originZ + 0.50;
    scarPatch(ctx, 5.25, 4.15, C.scarEarth, C.scarBurn);
    scarStrip(ctx, -1.14, -3.62, -1.14, 3.86, 0.16, C.strawDark);

    // The overturned cart sits behind the spill so the complete source-and-fan
    // story is presented broadside to the hero instead of as a low gold patch.
    put(ctx, ROLE.ORGANIC, BOX(), C.wood, 0, 0.92, -2.20,
      0.18, 0, 0.24, 2.75, 0.30, 1.36);
    put(ctx, ROLE.ORGANIC, BOX(), C.woodDark, -1.24, 1.18, -2.18,
      0.18, 0, 0.24, 0.16, 1.28, 1.42);
    put(ctx, ROLE.ORGANIC, BOX(), C.woodDark, 1.24, 1.18, -2.18,
      0.18, 0, 0.24, 0.16, 1.28, 1.42);
    beam(ctx, ROLE.ORGANIC, C.woodDark, -0.92, 0.20, -3.42,
      -0.92, 0.18, 0.12, 0.12, 8);
    beam(ctx, ROLE.ORGANIC, C.woodDark, 0.92, 0.20, -3.42,
      0.92, 0.18, 0.12, 0.12, 8);
    wheel(ctx, -1.58, -2.10, 1.04, 0, 0.12, false, 1.38);
    wheel(ctx, 1.68, -1.76, 0.72, 0.22, 0.94, true, 1.18);
    sack(ctx, -1.86, -0.58, -0.22, C.canvas);
    sack(ctx, -0.88, -0.38, 0.16, C.canvasDark);
    sack(ctx, 1.56, -0.32, -0.12, C.canvas);
    barrel(ctx, 2.42, -1.28, 0.18, true);

    const ca = groundPoint(ctx, -1.00, 0.070, -0.52);
    const cb = groundPoint(ctx, 1.04, 0.072, -0.52);
    const cc = groundPoint(ctx, 2.62, 0.074, 3.72);
    const cd = groundPoint(ctx, -2.76, 0.073, 3.60);
    builders[ctx.chunk][ROLE.CLOTH].quad(ca, cb, cc, cd, C.canvasDark);
    rolePieces[ROLE.CLOTH]++;

    // Overlapping low-poly mounds replace the former six orderly bands. The
    // loose clusters retain a broad tapered fan in silhouette without reading
    // as pale planks when the frame is reduced and desaturated.
    for (let row = 0; row < 6; row++) {
      const z1 = 0.24 + row * 0.66;
      const w1 = 0.79 + row * 0.37;
      const clusters = row < 2 ? 1 : 2;
      for (let c = 0; c < clusters; c++) {
        const side = clusters === 1 ? 0 : (c * 2 - 1);
        const jitter = Math.sin((row + 1) * 2.31 + c * 1.73);
        put(ctx, ROLE.ORGANIC, ICO(1), (row + c) & 1 ? C.grainDark : C.grain,
          side * w1 * 0.34 + jitter * 0.14,
          0.18 + (row % 3) * 0.018,
          z1 - 0.20 + jitter * 0.12,
          0.08 * row, 0.24 * (row + c), 0.04 * side,
          w1 * (clusters === 1 ? 1.12 : 0.70),
          0.18 + row * 0.012,
          0.46 + (row % 2) * 0.08);
      }
      const count = 3 + row;
      for (let j = 0; j < count; j++) {
        const t = count === 1 ? 0 : j / (count - 1);
        const jitter = Math.sin((row + 1) * 2.17 + j * 3.11);
        const x = (t * 2 - 1) * w1 * 0.88 + jitter * 0.14;
        const s = 0.23 + ((row * 3 + j) % 4) * 0.045;
        put(ctx, ROLE.ORGANIC, ICO(0), (row + j) % 3 ? C.grain : C.straw,
          x, 0.17, z1 - 0.18 + jitter * 0.10,
          0.08 * j, 0.21 * (row + j), 0,
          s * 1.45, 0.11 + (j % 3) * 0.024, s);
      }
    }
    ctx.x = originX;
    ctx.z = originZ;
  }

  function shieldBreach(ctx) {
    scarPatch(ctx, 5.10, 3.55, C.scarBlood, C.scarEarth);
    scarStrip(ctx, -4.58, -0.22, 4.56, 0.12, 0.22, C.scarBlood);

    // Eight planted shields form a continuous nine-metre wall with a clear
    // two-metre failure in the centre. Alternating plank pavises, rimmed rounds
    // and varied lean survives the quarter-resolution silhouette gate.
    const line = [
      [-4.20, -0.12, C.blueDark, 0.98, -0.09],
      [-3.16, 0.02, C.blue, 1.02, 0.04],
      [-2.10, -0.10, C.blueDark, 0.96, -0.13],
      [-1.04, 0.04, C.blue, 1.04, -0.23],
      [1.04, 0.06, C.red, 1.04, 0.24],
      [2.10, -0.08, C.redDark, 0.96, 0.14],
      [3.16, 0.04, C.red, 1.02, -0.04],
      [4.20, -0.10, C.redDark, 0.98, 0.09],
    ];
    for (let i = 0; i < line.length; i++) {
      const s = line[i];
      if (i === 1 || i === 3 || i === 4 || i === 6) {
        standingPavise(ctx, s[0], s[1], (i - 3.5) * 0.055, s[2], s[3], s[4]);
      } else {
        standingShield(ctx, s[0], s[1], (i - 3.5) * 0.055, s[2], s[3], s[4]);
      }
    }
    // Paired defensive rails visually bind each faction's shield bank while
    // ending cleanly at the breach. Four irregular posts keep them grounded.
    for (const y of [0.54, 1.18]) {
      beam(ctx, ROLE.ORGANIC, y > 1 ? C.wood : C.woodDark,
        -4.62, y, 0.18, -0.68, y, 0.08, 0.105, 7);
      beam(ctx, ROLE.ORGANIC, y > 1 ? C.wood : C.woodDark,
        0.68, y, 0.08, 4.62, y, 0.18, 0.105, 7);
    }
    for (const x of [-4.58, -0.72, 0.72, 4.58]) {
      beam(ctx, ROLE.ORGANIC, C.woodDark, x, 0.06, 0.20,
        x + (x < 0 ? 0.06 : -0.06), 1.54, 0.16, 0.10, 7);
    }
    // Two outward pikes frame the breach instead of repeating a body pile.
    spear(ctx, -1.18, 1.18, 4.65, 2.82, C.blueDark, false);
    spear(ctx, 1.22, 1.10, 4.62, 0.32, C.redDark, false);
    put(ctx, ROLE.METAL, BOX(), C.ironDark, 0.02, 0.18, 0.28,
      0.10, 0.42, 0.28, 0.66, 0.11, 0.22);
  }

  function casualtyKnot(ctx) {
    scarPatch(ctx, 3.6, 3.0, C.scarBlood, C.scarEarth);
    // A compact field-triage canopy and two empty evacuation litters replace
    // the exploded body pile. One casualty outside the opening supplies the
    // narrative detail without becoming the incident's silhouette owner.
    tarp(ctx, C.canvasDark, -1.86, -1.26, 1.86, 1.26, 2.46, 1.38);
    for (const x of [-1.86, 1.86]) {
      for (const z of [-1.26, 1.26]) {
        beam(ctx, ROLE.ORGANIC, C.wood, x, 0.05, z,
          x, 1.42, z, 0.085, 6);
      }
    }
    beam(ctx, ROLE.ORGANIC, C.woodPale, 0, 2.46, -1.34,
      0, 2.46, 1.34, 0.10, 7);
    litter(ctx, -0.84, -0.16, 0.06, C.blueDark, 0.96);
    litter(ctx, 0.84, 0.38, Math.PI - 0.10, C.redDark, 0.92);
    corpse(ctx, 2.34, -0.74, 0.22, C.blueDark, 1);
    shield(ctx, -2.42, 0.82, 0.55, C.blueDark, 0.28);
    const ma = groundPoint(ctx, -1.76, 2.22, -1.28);
    const mb = groundPoint(ctx, -0.58, 2.02, -1.28);
    const mc = groundPoint(ctx, -0.82, 1.30, -1.28);
    const md = groundPoint(ctx, -1.76, 1.46, -1.28);
    builders[ctx.chunk][ROLE.CLOTH].quad(ma, mb, mc, md, C.redDark);
    rolePieces[ROLE.CLOTH]++;
  }

  function commandFall(ctx) {
    scarPatch(ctx, 4.0, 2.6, C.scarBlood, C.scarBurn);
    corpse(ctx, 0.22, -0.26, 0.08, C.red, 2);
    corpse(ctx, 1.62, 0.82, Math.PI - 0.18, C.blueDark, 3);

    // A broken but still elevated command standard is the dominant read. Its
    // torn red field falls toward the two bodies while a signal drum anchors
    // the base, avoiding the old low corpse-and-shield island.
    beam(ctx, ROLE.ORGANIC, C.woodPale, -1.84, 0.08, -0.52,
      -0.10, 4.12, -0.12, 0.105, 7);
    const a = groundPoint(ctx, -0.34, 3.82, -0.14);
    const b = groundPoint(ctx, 2.10, 3.36, -0.08);
    const c = groundPoint(ctx, 1.38, 1.96, 0.02);
    const d = groundPoint(ctx, -0.78, 2.46, -0.10);
    builders[ctx.chunk][ROLE.CLOTH].quad(a, b, c, d, C.red);
    rolePieces[ROLE.CLOTH]++;
    put(ctx, ROLE.CLOTH, CYL(10, 1), C.redDark, -2.30, 0.58, 0.74,
      Math.PI * 0.5, -0.06, 0, 0.58, 0.82, 0.58);
    for (const o of [-0.35, 0.35]) put(ctx, ROLE.METAL, TUBE(10, 1), C.bronze,
      -2.30, 0.58, 0.74 + o, Math.PI * 0.5, -0.06, 0, 0.62, 0.09, 0.62);
    beam(ctx, ROLE.ORGANIC, C.woodDark, -2.30, 0.58, -0.05,
      -2.30, 0.58, 1.54, 0.075, 6);
    shield(ctx, -1.30, -1.32, -0.24, C.redDark, 0.72);
    scarStrip(ctx, -3.25, 0.22, 2.95, 0.22, 0.15, C.scarBlood);
  }

  function spearLineBreak(ctx) {
    scarPatch(ctx, 4.9, 3.25, C.scarBurn, C.scarEarth);

    // A collapsed pike hedge replaces the old corpse-and-round-shield pile.
    // Three intact spears establish one formation heading; four visibly
    // snapped pairs keep that heading but open a jagged central failure seam.
    // The staggered centres make a diagonal comb in aerial views rather than
    // another circular casualty island.
    // Five survivors rise behind the debris as an uneven pike comb. The open
    // centre is the broken line; the low pieces below now have a clear parent
    // formation instead of reading as unrelated sticks.
    const standing = [
      [-3.30, -0.76, 2.86, -0.18, C.blueDark],
      [-1.92, -0.68, 3.42, 0.10, C.blueDark],
      [-0.72, -0.62, 2.58, -0.08, C.blue],
      [1.72, -0.58, 3.18, 0.14, C.redDark],
      [3.12, -0.52, 2.72, -0.12, C.redDark],
    ];
    for (let i = 0; i < standing.length; i++) {
      const s = standing[i];
      beam(ctx, ROLE.ORGANIC, C.wood, s[0], 0.06, s[1],
        s[0] + s[3], s[2], s[1] + 0.10, 0.070, 6);
      put(ctx, ROLE.METAL, CONE(5), C.ironPale,
        s[0] + s[3], s[2] + 0.18, s[1] + 0.10,
        0, 0, 0, 0.13, 0.36, 0.13);
    }

    const snapped = [
      [-3.55, -1.72, -0.62, -1.42, 0.18, -1.30, 2.96, -0.98],
      [-3.36, -1.02, -0.42, -0.72, 0.36, -0.56, 3.32, -0.12],
      [-3.12, 0.36, -0.22, 0.74, 0.48, 0.88, 3.28, 1.34],
      [-2.92, 1.08, -0.02, 1.46, 0.70, 1.62, 3.50, 2.12],
    ];
    for (let i = 0; i < snapped.length; i++) {
      const p = snapped[i];
      beam(ctx, ROLE.ORGANIC, i & 1 ? C.woodDark : C.wood,
        p[0], 0.105, p[1], p[2], 0.105, p[3], 0.070, 6);
      beam(ctx, ROLE.ORGANIC, i & 1 ? C.wood : C.woodDark,
        p[4], 0.105, p[5], p[6], 0.105, p[7], 0.070, 6);
    }

    // A leather lashing still crosses every shaft, so the low pieces read as
    // one failed formation rather than procedural stick scatter.
    beam(ctx, ROLE.ORGANIC, C.leather, -1.92, 0.135, -2.52,
      -1.58, 0.135, -0.76, 0.075, 6);
    beam(ctx, ROLE.ORGANIC, C.leather, -1.58, 0.135, -0.76,
      -1.22, 0.135, 0.92, 0.075, 6);
    beam(ctx, ROLE.ORGANIC, C.leather, -1.22, 0.135, 0.92,
      -0.78, 0.135, 2.62, 0.075, 6);

    // The fallen blue standard cuts across the hedge at a single dominant
    // diagonal. Its broad torn cloth and the opposing red end pennant retain
    // faction readability without reintroducing shields or bodies.
    beam(ctx, ROLE.ORGANIC, C.woodPale, -2.72, 0.145, 2.42,
      2.90, 0.145, -1.92, 0.105, 7);
    const a = groundPoint(ctx, -1.34, 0.112, 1.36);
    const b = groundPoint(ctx, 0.30, 0.112, 0.10);
    const c = groundPoint(ctx, 1.04, 0.114, 0.36);
    const d = groundPoint(ctx, -0.20, 0.114, 1.52);
    builders[ctx.chunk][ROLE.CLOTH].quad(a, b, c, d, C.blueDark);
    rolePieces[ROLE.CLOTH]++;
    const ra = groundPoint(ctx, 1.58, 0.116, -0.88);
    const rb = groundPoint(ctx, 2.44, 0.116, -1.54);
    const rc = groundPoint(ctx, 2.24, 0.118, -0.58);
    const rd = groundPoint(ctx, 1.86, 0.118, -0.42);
    builders[ctx.chunk][ROLE.CLOTH].quad(ra, rb, rc, rd, C.redDark);
    rolePieces[ROLE.CLOTH]++;

    scarStrip(ctx, -3.90, -1.52, 3.72, -0.62, 0.17, C.scarBlood);
    scarStrip(ctx, -3.62, 0.04, 3.82, 0.82, 0.14, C.scarBurn);
    scarStrip(ctx, -3.22, 1.50, 3.68, 2.12, 0.12, C.scarBlood);
  }

  function baggageAmbush(ctx) {
    scarPatch(ctx, 4.0, 2.85, C.scarBlood, C.scarBurn);
    // The ambushed baggage cart is pitched onto one side at route scale, with
    // one wheel held high. This creates a wreck silhouette before the viewer
    // resolves any body or crate detail.
    put(ctx, ROLE.ORGANIC, BOX(), C.wood, -0.38, 0.94, -0.10,
      0.52, -0.10, 0.12, 3.18, 0.34, 1.52);
    put(ctx, ROLE.ORGANIC, BOX(), C.woodDark, -0.42, 1.44, -0.66,
      0.52, -0.10, 0.12, 3.24, 0.18, 0.16);
    put(ctx, ROLE.ORGANIC, BOX(), C.woodDark, -0.42, 1.18, 0.70,
      0.52, -0.10, 0.12, 3.24, 0.18, 0.16);
    wheel(ctx, -1.20, -0.96, 1.04, -0.12, 0.10, false, 1.36);
    wheel(ctx, 0.92, 1.40, 0.72, 0.32, 0.96, true, 1.16);
    beam(ctx, ROLE.ORGANIC, C.woodDark, 1.08, 0.20, -0.62,
      4.12, 0.14, -0.82, 0.11, 7);
    beam(ctx, ROLE.ORGANIC, C.woodDark, 1.04, 0.20, 0.54,
      4.02, 0.14, 0.86, 0.11, 7);
    crate(ctx, -2.24, 1.20, 0.28, 0.96, 0);
    crate(ctx, -2.78, 0.12, -0.36, 0.82, 0);
    crate(ctx, 2.02, -1.34, 0.18, 0.72, 0);
    sack(ctx, 0.75, -1.32, 0.16, C.canvasDark);
    sack(ctx, 1.42, -1.02, -0.24, C.canvas);
    corpse(ctx, 2.20, 0.42, 0.10, C.blueDark, 1);
    corpse(ctx, -0.92, -1.84, 0.18, C.redDark, 0);
    spear(ctx, 1.20, 1.32, 3.55, -0.18, C.blueDark, false);
    tarp(ctx, C.blueDark, -0.72, -0.32, 1.56, 0.72, 0.78, 0.26);
  }

  function lastStand(ctx) {
    scarPatch(ctx, 5.35, 4.35, C.scarBurn, C.scarBlood);

    // An open horseshoe palisade faces the hero. Alternating burned stakes,
    // braces and a torn five-metre command standard make a redoubt silhouette,
    // not an abstract low triangle or another corpse/shield island.
    const stakes = [
      [-3.75, -2.45, 2.72], [-2.55, -2.58, 3.04], [-1.30, -2.66, 2.54],
      [0, -2.72, 3.18], [1.28, -2.64, 2.66], [2.55, -2.54, 3.00],
      [3.74, -2.42, 2.58], [-3.82, -1.20, 2.46], [-3.86, 0.08, 2.90],
      [-3.78, 1.34, 2.42], [3.82, -1.18, 2.72], [3.86, 0.12, 2.48],
      [3.78, 1.38, 2.86],
    ];
    for (let i = 0; i < stakes.length; i++) {
      const s = stakes[i];
      beam(ctx, ROLE.ORGANIC, i % 3 ? C.woodDark : C.scarBurn,
        s[0], 0.08, s[1], s[0] + ((i % 3) - 1) * 0.10, s[2], s[1], 0.14, 7);
      put(ctx, ROLE.ORGANIC, CONE(5), C.scarBurn,
        s[0] + ((i % 3) - 1) * 0.10, s[2] + 0.16, s[1],
        0, 0, 0, 0.19, 0.38, 0.19);
    }
    beam(ctx, ROLE.ORGANIC, C.woodDark, -3.80, 1.34, -2.38,
      -3.76, 1.18, 1.42, 0.13, 8);
    beam(ctx, ROLE.ORGANIC, C.woodDark, 3.80, 1.28, -2.36,
      3.76, 1.20, 1.42, 0.13, 8);
    beam(ctx, ROLE.ORGANIC, C.wood, -3.48, 0.20, -2.48,
      -1.58, 2.36, -2.58, 0.11, 7);
    beam(ctx, ROLE.ORGANIC, C.wood, 3.48, 0.20, -2.46,
      1.54, 2.28, -2.60, 0.11, 7);

    beam(ctx, ROLE.ORGANIC, C.woodPale, -0.28, 0.06, -2.82,
      -0.28, 5.18, -2.82, 0.10, 8);
    const fa = groundPoint(ctx, -0.20, 4.98, -2.80);
    const fb = groundPoint(ctx, 2.12, 4.38, -2.80);
    const fc = groundPoint(ctx, 1.42, 2.94, -2.80);
    const fd = groundPoint(ctx, -0.20, 3.38, -2.80);
    builders[ctx.chunk][ROLE.CLOTH].quad(fa, fb, fc, fd, C.red, C.redDark);
    rolePieces[ROLE.CLOTH]++;
    const ta = groundPoint(ctx, -2.96, 0.10, -0.82);
    const tb = groundPoint(ctx, -0.72, 0.10, -1.36);
    const tc = groundPoint(ctx, 0.58, 0.11, 0.92);
    const td = groundPoint(ctx, -2.22, 0.10, 1.18);
    builders[ctx.chunk][ROLE.CLOTH].quad(ta, tb, tc, td, C.redDark);
    rolePieces[ROLE.CLOTH]++;
    spear(ctx, -1.42, 1.30, 3.42, 0.22, C.redDark, true);
    spear(ctx, 1.42, 1.12, 3.12, 2.86, 0, true);
    scarStrip(ctx, -3.80, -2.50, 3.80, -2.50, 0.20, C.scarBurn);
    scarStrip(ctx, -3.82, -2.42, -3.78, 1.44, 0.18, C.scarBlood);
    scarStrip(ctx, 3.82, -2.42, 3.78, 1.44, 0.18, C.scarBurn);
  }

  const recipes = [
    // Primary route incidents are placed first so their authored negative
    // space cannot be consumed by a distant district tableau. Their fixed
    // anchors sit beside, never on, the central objective approaches.
    { name: 'canvas_field_cache', kind: 'camp', chunk: 0, fn: canvasFieldCache,
      primary: true, anchor: TABLEAUX.anchors.canvas_field_cache, footprint: 4.8, grass: [5.9, 5.0] },
    { name: 'shield_breach', kind: 'battle', chunk: 0, fn: shieldBreach,
      primary: true, anchor: TABLEAUX.anchors.shield_breach, footprint: 5.1, grass: [5.9, 4.8] },
    { name: 'last_stand', kind: 'battle', chunk: 0, fn: lastStand,
      primary: true, anchor: TABLEAUX.anchors.last_stand, footprint: 5.3, grass: [6.2, 5.2] },
    { name: 'field_repair_yard', kind: 'camp', chunk: 1, fn: fieldRepairYard,
      primary: true, anchor: TABLEAUX.anchors.field_repair_yard, footprint: 5.0, grass: [6.0, 5.0] },
    { name: 'grain_spill', kind: 'field', chunk: 1, fn: grainSpill,
      primary: true, anchor: TABLEAUX.anchors.grain_spill, footprint: 5.2, grass: [9.4, 7.2] },

    { name: 'broken_wagon_supply', kind: 'camp', chunk: 0, fn: brokenWagonSupply,
      footprint: 4.4, grass: [4.8, 3.5] },
    { name: 'harvest_sledge', kind: 'field', chunk: 0, fn: harvestSledge,
      footprint: 4.1, grass: [4.5, 3.0] },
    { name: 'casualty_knot', kind: 'battle', chunk: 0, fn: casualtyKnot,
      footprint: 4.1, grass: [4.4, 3.7] },
    { name: 'abandoned_checkpoint', kind: 'camp', chunk: 1, fn: abandonedCheckpoint,
      footprint: 4.0, grass: [4.5, 3.5] },
    { name: 'spear_line_break', kind: 'battle', chunk: 1, fn: spearLineBreak,
      footprint: 4.6, grass: [5.0, 3.4] },
    { name: 'baggage_ambush', kind: 'battle', chunk: 1, fn: baggageAmbush,
      footprint: 4.4, grass: [4.8, 3.9] },
    { name: 'command_fall', kind: 'battle', chunk: 1, fn: commandFall,
      footprint: 4.3, grass: [4.7, 3.6] },
  ];

  function extremes(list, lowCount, highCount) {
    const sorted = [...list].sort((a, b) => a.x - b.x || a.z - b.z);
    return [sorted.slice(0, lowCount), sorted.slice(Math.max(lowCount, sorted.length - highCount))];
  }

  const campSides = extremes(anchors.camps, 2, 2);
  const fieldSides = extremes(anchors.fields, 1, 1);
  const battleSides = extremes(anchors.battles, 3, 3);
  const sidePools = [
    { camp: campSides[0], field: fieldSides[0], battle: battleSides[0] },
    { camp: campSides[1], field: fieldSides[1], battle: battleSides[1] },
  ];
  const usedByKind = [{ camp: 0, field: 0, battle: 0 }, { camp: 0, field: 0, battle: 0 }];

  function validFootprint(x, z, kind, radius, primary = false) {
    if (Math.abs(x) > T.half - 10 || Math.abs(z) > T.half - 10) return false;
    if (siteMask(x, z) > 0.02 || T.riverDist(x, z) < 12) return false;
    if (T.roadWeight(x, z) > 0.055 || T.slopeAt(x, z) > 0.035) return false;
    if (Math.hypot(x, z) < (kind === 'battle' ? 10 : 18)) return false;
    if (primary && (Math.hypot(x, z) < 12 || Math.hypot(x, z) > 30)) return false;
    const allowed = kind === 'battle' ? [0, 1, 2, 4] : [0, 1];
    if (!allowed.includes(T.materialAt(x, z))) return false;
    let hMin = T.heightAt(x, z), hMax = hMin;
    for (let i = 0; i < 12; i++) {
      const a = i * TAU / 12;
      const sx = x + Math.cos(a) * radius, sz = z + Math.sin(a) * radius;
      if (siteMask(sx, sz) > 0.06 || T.riverDist(sx, sz) < 10.8 ||
          T.roadWeight(sx, sz) > 0.16 || T.slopeAt(sx, sz) > 0.055) return false;
      const h = T.heightAt(sx, sz);
      hMin = Math.min(hMin, h);
      hMax = Math.max(hMax, h);
    }
    return hMax - hMin <= 0.62;
  }

  function nearestCenter(x, z) {
    let nearest = 80;
    for (const t of tableaux) nearest = Math.min(nearest, Math.hypot(x - t.x, z - t.z));
    return nearest;
  }

  function footprintHeightSpan(x, z, radius) {
    let hMin = T.heightAt(x, z), hMax = hMin;
    for (let i = 0; i < 12; i++) {
      const a = i * TAU / 12;
      const h = T.heightAt(x + Math.cos(a) * radius, z + Math.sin(a) * radius);
      hMin = Math.min(hMin, h);
      hMax = Math.max(hMax, h);
    }
    return hMax - hMin;
  }

  function findCenter(anchor, recipe, rng) {
    const { kind, footprint, primary, name } = recipe;
    if (primary) {
      let best = null, bestScore = -1e9;
      // First candidate is the art-directed location. Subsequent samples find
      // the nearest valid flat shoulder without leaving the active camera ring.
      for (let i = 0; i < 192; i++) {
        const a = rng.next() * TAU;
        const r = i === 0 ? 0 : Math.sqrt(rng.next()) * anchor.r;
        const x = anchor.x + Math.cos(a) * r;
        const z = anchor.z + Math.sin(a) * r;
        if (!validFootprint(x, z, kind, footprint, true)) continue;
        const nearest = nearestCenter(x, z);
        if (nearest < 16) continue;
        const score = -r + nearest * 0.001;
        if (score > bestScore) { bestScore = score; best = { x, z }; }
      }
      if (best) return best;
      // A bounded second shoulder search tolerates terrain revisions but can
      // never migrate the incident out of the 12-30 m active-camera band.
      for (let i = 0; i < 512; i++) {
        const a = rng.next() * TAU;
        const r = Math.sqrt(rng.next()) * (anchor.r + 5);
        const x = anchor.x + Math.cos(a) * r;
        const z = anchor.z + Math.sin(a) * r;
        if (!validFootprint(x, z, kind, footprint, true) || nearestCenter(x, z) < 16) continue;
        const score = -r;
        if (score > bestScore) { bestScore = score; best = { x, z }; }
      }
      if (best) return best;
      throw new Error(`Primary battlefield tableau ${name} has no valid active-route footprint`);
    }

    let best = null, bestScore = -1e9;
    for (let i = 0; i < 96; i++) {
      const a = rng.next() * TAU;
      const r = i === 0 ? 0 : Math.sqrt(rng.next()) * Math.min(8.5, anchor.r * 0.62);
      const x = anchor.x + Math.cos(a) * r;
      const z = anchor.z + Math.sin(a) * r;
      if (!validFootprint(x, z, kind, footprint)) continue;
      const nearest = nearestCenter(x, z);
      // Adjacent incidents need enough negative space to read as authored
      // events, not one repeated procedural debris knot.
      if (nearest < 16) continue;
      const score = nearest - r * 0.08;
      if (score > bestScore) { bestScore = score; best = { x, z }; }
    }
    if (best) return best;
    // Preserve every centre rule while widening around a locally exhausted
    // anchor (the far-west road/site junction can consume its whole small
    // radius). This still keeps the incident in the anchor's district.
    for (let i = 0; i < 256; i++) {
      const a = rng.next() * TAU, r = Math.sqrt(rng.next()) * Math.max(30, anchor.r * 1.8);
      const x = anchor.x + Math.cos(a) * r, z = anchor.z + Math.sin(a) * r;
      if (!validFootprint(x, z, kind, footprint)) continue;
      const nearest = nearestCenter(x, z);
      if (nearest >= 16) return { x, z };
    }
    // Last-resort district search. It is deterministic, remains on the same
    // spatial half as the authored anchor, and uses the identical strict
    // footprint test; it cannot silently put a tableau in water or a fort.
    const side = anchor.x < 0 ? -1 : 1;
    let districtBest = null, districtScore = -1;
    for (let i = 0; i < 512; i++) {
      const x = side * (24 + rng.next() * (T.half - 46));
      const z = (rng.next() - 0.5) * (T.half * 2 - 28);
      if (!validFootprint(x, z, kind, footprint)) continue;
      const nearest = nearestCenter(x, z);
      if (nearest < 16) continue;
      if (nearest > districtScore) { districtScore = nearest; districtBest = { x, z }; }
    }
    if (districtBest) return districtBest;
    throw new Error(`Battlefield tableau placement failed near ${anchor.x.toFixed(1)},${anchor.z.toFixed(1)}`);
  }

  for (let i = 0; i < recipes.length; i++) {
    const recipe = recipes[i];
    /* A tableau this arena does not carry is skipped, not placed somewhere it
     * does not belong and not thrown on. `continue` keeps `i`, so every other
     * recipe's rng fork (0x100 + i*37) is unchanged — which is why skipping on
     * Arena II cannot move anything on Lembah Brantas, where nothing skips. */
    if (!TABLEAUX.kinds.includes(recipe.kind)) continue;
    if (recipe.primary && !recipe.anchor) continue;
    let anchor = recipe.anchor;
    if (!anchor) {
      const pool = sidePools[recipe.chunk][recipe.kind];
      const pick = usedByKind[recipe.chunk][recipe.kind]++;
      anchor = pool[pick % pool.length];
    }
    const rng = placementRng.fork(0x100 + i * 37);
    const center = findCenter(anchor, recipe, rng);
    // Primary silhouettes are composed broadside to the central hero/camera.
    // Their local +Z is the open/readable face; distant incidents retain their
    // authored district headings.
    const heading = recipe.primary
      ? Math.atan2(-center.x, -center.z) + rng.range(-0.035, 0.035)
      : anchor.a + rng.range(-0.18, 0.18);
    const ctx = {
      x: center.x, z: center.z, chunk: recipe.chunk, rng,
      heading,
    };
    recipe.fn(ctx);
    const grassLocal = recipe.grass;
    const ca = Math.abs(Math.cos(heading)), sa = Math.abs(Math.sin(heading));
    const grassRadiusX = grassLocal[0] * ca + grassLocal[1] * sa;
    const grassRadiusZ = grassLocal[0] * sa + grassLocal[1] * ca;
    tableaux.push({
      name: recipe.name, kind: recipe.kind, chunk: CHUNK_NAMES[recipe.chunk],
      x: Number(ctx.x.toFixed(3)), z: Number(ctx.z.toFixed(3)),
      heading: Number(ctx.heading.toFixed(4)),
      primary: !!recipe.primary,
      activeDistance: Number(Math.hypot(ctx.x, ctx.z).toFixed(3)),
      footprintRadius: recipe.footprint,
      footprintHeightSpan: Number(footprintHeightSpan(ctx.x, ctx.z, recipe.footprint).toFixed(3)),
      centerSlope: Number(T.slopeAt(ctx.x, ctx.z).toFixed(4)),
      grassRadiusX: Number(grassRadiusX.toFixed(3)),
      grassRadiusZ: Number(grassRadiusZ.toFixed(3)),
    });
  }

  let minTableauClearance = Infinity;
  for (let i = 0; i < tableaux.length; i++) {
    for (let j = i + 1; j < tableaux.length; j++) {
      minTableauClearance = Math.min(minTableauClearance,
        Math.hypot(tableaux[i].x - tableaux[j].x, tableaux[i].z - tableaux[j].z));
    }
  }
  const primaryTableaux = tableaux.filter(t => t.primary);

  const geometries = [];
  let triangles = 0;
  for (let chunk = 0; chunk < 2; chunk++) {
    for (let role = 0; role < 3; role++) {
      const geometry = builders[chunk][role].toGeometry();
      const mesh = new THREE.Mesh(geometry, materials[role]);
      mesh.name = `tableaux_${CHUNK_NAMES[chunk]}_${ROLE_NAMES[role]}`;
      mesh.castShadow = false;
      mesh.receiveShadow = true;
      mesh.frustumCulled = true;
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      group.add(mesh);
      geometries.push(geometry);
      triangles += triCount(geometry);
    }
  }
  const scarGeometry = scars.toGeometry();
  const scarMesh = new THREE.Mesh(scarGeometry, scarMat);
  scarMesh.name = 'tableaux_scar_contact';
  scarMesh.castShadow = false;
  scarMesh.receiveShadow = false;
  scarMesh.frustumCulled = true;
  scarMesh.renderOrder = 1;
  scarMesh.matrixAutoUpdate = false;
  scarMesh.updateMatrix();
  group.add(scarMesh);
  geometries.push(scarGeometry);
  triangles += triCount(scarGeometry);
  wheelRim.dispose();

  const stats = Object.freeze({
    count: tableaux.length,
    chunks: 2,
    drawCalls: group.children.length,
    triangles: Math.round(triangles),
    geometries: geometries.length,
    materials: materials.length,
    textures: 0,
    shadowCasters: 0,
    updates: 0,
    scarPatches,
    scarQuads,
    scarLift: SCAR_LIFT,
    scarOpacity: scarMat.opacity,
    minShaftRadius: SHAFT_R,
    primaryCount: primaryTableaux.length,
    primaryDistanceMin: Number(Math.min(...primaryTableaux.map(t => t.activeDistance)).toFixed(3)),
    primaryDistanceMax: Number(Math.max(...primaryTableaux.map(t => t.activeDistance)).toFixed(3)),
    minTableauClearance: Number(minTableauClearance.toFixed(3)),
    maxFootprintHeightSpan: Number(Math.max(...tableaux.map(t => t.footprintHeightSpan)).toFixed(3)),
    maxCenterSlope: Number(Math.max(...tableaux.map(t => t.centerSlope)).toFixed(4)),
    grassExclusions: tableaux.length,
    rolePieces: Object.freeze({
      organic: rolePieces[0], metal: rolePieces[1], cloth: rolePieces[2],
    }),
    legacyFamiliesRetired: Object.freeze(['hay', 'cart', 'corpse', 'spear', 'debris', 'crate']),
    legacyNominalInstancesRetired: 722,
    tableaux: Object.freeze(tableaux),
  });

  /* TWO KINDS OF RULE, which used to be one expression.
   *
   * BUDGETS are ceilings a render cost must stay under, and they hold on every
   * arena: draw calls, triangles, geometries, materials, no textures, no
   * shadow casters, shafts thick enough to read, footprints that sit flat.
   *
   * EXPECTATIONS are one arena's content. "primaryCount !== 5" and
   * "grassExclusions !== 12" were written here as if they were budgets; they
   * are Lembah Brantas's set-pieces, pinned by exact equality. Arena II
   * correctly places no primary set-pieces and failed on them at boot. They
   * now come from the arena record (TABLEAUX.expect), at the SAME strictness
   * for Lembah Brantas. The primary camera-band check only applies where
   * there are primaries: Math.min() of an empty list is Infinity, so it used
   * to pass vacuously, and a check that passes vacuously is not a check. */
  const X = TABLEAUX.expect || {};
  const over = [];
  if (stats.drawCalls > 7) over.push('drawCalls');
  if (stats.triangles > 45000) over.push('triangles');
  if (stats.geometries > 7) over.push('geometries');
  if (stats.materials > 4) over.push('materials');
  if (stats.textures !== 0) over.push('textures');
  if (stats.shadowCasters !== 0) over.push('shadowCasters');
  if (stats.minShaftRadius < 0.055) over.push('minShaftRadius');
  if (stats.count && stats.minTableauClearance < 15.99) over.push('minTableauClearance');
  if (stats.count && stats.maxFootprintHeightSpan > 0.62) over.push('maxFootprintHeightSpan');
  if (X.count && (stats.count < X.count[0] || stats.count > X.count[1])) over.push(`count (want ${X.count[0]}-${X.count[1]})`);
  if (X.primary !== undefined && stats.primaryCount !== X.primary) over.push(`primaryCount (want ${X.primary})`);
  if (X.grassExclusions !== undefined && stats.grassExclusions !== X.grassExclusions) over.push(`grassExclusions (want ${X.grassExclusions})`);
  if (stats.primaryCount > 0 && (stats.primaryDistanceMin < 12 || stats.primaryDistanceMax > 30)) over.push('primaryDistance');
  if (over.length) {
    throw new Error(`Battlefield tableaux budget violation [${over.join(', ')}]: ${JSON.stringify(stats)}`);
  }

  group.userData.tableauStats = stats;
  return { group, stats };
}
